/**
 * #979 — a card's annotation controls, in the contract's header order:
 * placement badge → **Source → Note** → Alarm → Pin
 * (`docs/260926FeatureDeliveryContracts.md` §Sources and priority).
 *
 * The one place that order is written: every card that carries annotations
 * passes `<CardAnnotations subject={…} />` into its `annotations` slot (#997)
 * rather than composing the two controls itself, so the order cannot drift
 * between the row components and the Tracker's direct renders.
 *
 * Callers pass it only when `useCardAnnotationsActive()` (below), so on a
 * route that mounts neither provider no annotation slot is filled at all.
 */
import type { ReactNode } from 'react';
import type { CardNoteSubject, CardNoteVenue } from '@/lib/cardNotes/types';
import type { TradingVenue } from '@/lib/venues';
import { CardNoteControl } from './cardNotes/CardNoteControl';
import { useCardNotes } from './cardNotes/CardNotesProvider';
import { CardSourceControl } from './cardSources/CardSourceControl';
import { useCardSources } from './cardSources/CardSourcesProvider';

/**
 * Whether this page shows annotations at all (a Notes or Sources provider is
 * mounted). Cards pass `CardAnnotations` only when it is, so a page without
 * providers (the org detail route, for one) renders every card header
 * exactly as before, including the layout (#979's second row is opt-in).
 */
export function useCardAnnotationsActive(): boolean {
  const notes = useCardNotes();
  const sources = useCardSources();
  return notes != null || sources != null;
}

/** #1226 — card notes and sources are keyed by the Kalshi and international
 *  Polymarket venues (their scope is #976's); a Polymarket US row carries no
 *  annotations yet, so its slot stays empty. K/P rows get the controls. */
export function cardAnnotationsFor(subject:
  | { kind: 'order'; venue: TradingVenue; venueOrderId: string }
  | { kind: 'position'; venue: TradingVenue; marketId: string; outcome: 'yes' | 'no' }): ReactNode {
  if (subject.venue === 'polymarket_us') return undefined;
  const venue: CardNoteVenue = subject.venue;
  return subject.kind === 'order'
    ? <CardAnnotations subject={{ kind: 'order', venue, venueOrderId: subject.venueOrderId }} />
    : <CardAnnotations subject={{ kind: 'position', venue, marketId: subject.marketId, outcome: subject.outcome }} />;
}

export function CardAnnotations({ subject }: { subject: CardNoteSubject }) {
  return (
    <>
      <CardSourceControl subject={subject} />
      <CardNoteControl subject={subject} />
    </>
  );
}
